import { useState, useEffect, RefObject, Fragment } from 'react';
import { toast } from 'react-toastify';

import * as E from '@pages/Equipment/Equipment.style';
import theme from '@styles/theme';
import useDnD from '@hooks/useDnD';
import { selectedPlaylistHeader } from '@lib/data';

import { FlexBox, RegularFont } from '@components/styled/StyledComponents';
import { InfoSection } from '@components/share/Info/InfoSection';
import TableHeader from '@components/share/Table/TableHeader';
import { EmptyTable, TableBody } from '@components/share/Table/table.style';
import StyledCheckbox from '@components/styled/StyledCheckbox';
import StyledButton from '@components/styled/StyledButton';
import BannerListRow from './BannerListRow';
import PlaylistRow from './PlaylistRow';

type PlaylistSectionProps = {
    sectionIndex: number;
    sectionSubTitle: string;
    imageLists: Record<string, any[]>;
    videoLists: Record<string, any[]>;
    noticeLists: Record<string, any[]>;
    toggleImage: (sectionKey: string, value: any, options: any[]) => void;
    toggleVideo: (sectionKey: string, value: any, options: any[]) => void;
    toggleNotice: (sectionKey: string, value: any, options: any[]) => void;
    imageData: any[];
    videoData: any[];
    noticeData: any[];
};

const PlaylistSection = ({
    sectionIndex,
    sectionSubTitle,
    imageLists, // 체크박스 선택된 이미지 데이터
    videoLists, // 체크박스 선택된 비디오 데이터
    noticeLists, // 체크박스 선택된 공지 데이터
    toggleImage, // 항목 별 토글 함수
    toggleVideo, // 항목 별 토글 함수
    toggleNotice, // 항목 별 토글 함수
    imageData = [], // 전체 이미지 데이터
    videoData = [], // 전체 비디오 데이터
    noticeData = [] // 전체 공지 데이터
}: PlaylistSectionProps) => {
    const [selectedList, setSelectedList] = useState<any[]>([]); // 플레이리스트
    const [aspect, setAspect] = useState<number>(16 / 9); // 이미지, 썸네일 비율

    const { findList, moveList, drop } = useDnD(
        selectedList,
        setSelectedList,
        'id'
    );

    const availablePlaylistData = [
        {
            type: 'image',
            title: '배너 이미지',
            toggleFn: toggleImage,
            checkedList: imageLists,
            totalList: imageData
        },
        {
            type: 'video',
            title: '배너 영상',
            toggleFn: toggleVideo,
            checkedList: videoLists,
            totalList: videoData
        },
        {
            type: 'notice',
            title: '공지',
            toggleFn: toggleNotice,
            checkedList: noticeLists,
            totalList: noticeData
        }
    ];

    // 이미지 비율 설정
    useEffect(() => {
        if (sectionSubTitle.includes('배너')) {
            setAspect(16 / 9);
        } else if (sectionSubTitle.includes('전면')) {
            setAspect(9 / 16);
        } else if (sectionSubTitle.includes('정방형')) {
            setAspect(1);
        }
    }, [sectionSubTitle]);

    // 선택된 미디어 플레이리스트 반영
    useEffect(() => {
        setSelectedList([
            ...(imageLists[`section${sectionIndex + 1}`] || []),
            ...(videoLists[`section${sectionIndex + 1}`] || []),
            ...(noticeLists[`section${sectionIndex + 1}`] || [])
        ]);
    }, [imageLists, videoLists, noticeLists, sectionIndex]);

    const onSubmit = () => {
        try {
            toast.success('저장되었습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <InfoSection
            title={`구간 ${sectionIndex + 1} (${sectionSubTitle})`}
            fontFamily="PretendardMedium"
            margin="0 0 20px"
        >
            <FlexBox $alignItems="flex-start" $gap={20}>
                <E.SectionWrapper $width="305px">
                    {availablePlaylistData.map((data, index) => {
                        // 항목 없을 시 숨김 처리
                        if (!data.totalList.length) return null;
                        return (
                            <Fragment key={`${index}-banner-section`}>
                                <E.SectionHeader>
                                    <RegularFont>{data.title}</RegularFont>
                                    <StyledCheckbox
                                        checkboxId={`section${
                                            sectionIndex + 1
                                        }-${data.type}-check-all`}
                                        name={`section${sectionIndex + 1}-${
                                            data.type
                                        }-checkbox`}
                                        label="전체선택"
                                        value={''}
                                        onChange={(e) =>
                                            data.toggleFn(
                                                `section${sectionIndex + 1}`,
                                                e.target.value,
                                                data.totalList
                                            )
                                        }
                                        checked={
                                            (
                                                data.checkedList[
                                                    `section${sectionIndex + 1}`
                                                ] ?? []
                                            ).length === data.totalList.length
                                        }
                                        SQUARE
                                    />
                                </E.SectionHeader>
                                <E.SectionContents $margin="0 0 32px">
                                    {data.totalList.map((list, index) => (
                                        <BannerListRow
                                            key={`${index}-section${
                                                sectionIndex + 1
                                            }-${data.type}-checkbox`}
                                            name={`section${sectionIndex + 1}-${
                                                data.type
                                            }-checkbox`}
                                            data={list}
                                            list={
                                                data.checkedList[
                                                    `section${sectionIndex + 1}`
                                                ]
                                            }
                                            optionLists={data.totalList}
                                            sectionIndex={sectionIndex}
                                            toggleCheckbox={data.toggleFn}
                                            imgAspect={aspect}
                                        />
                                    ))}
                                </E.SectionContents>
                            </Fragment>
                        );
                    })}
                </E.SectionWrapper>
                <E.SectionWrapper $width="630px">
                    <RegularFont
                        $margin="0 0 12px"
                        $fontColor={theme.colors.positiveBlue}
                    >
                        재생할 플레이리스트입니다.
                    </RegularFont>
                    <TableHeader
                        header={selectedPlaylistHeader}
                        bgColor={theme.colors.ultraLightGrayBorder}
                    />
                    <TableBody
                        ref={drop as unknown as RefObject<HTMLUListElement>}
                    >
                        {selectedList.length > 0 ? (
                            selectedList.map((data, index) => (
                                <PlaylistRow
                                    key={index}
                                    index={index}
                                    header={selectedPlaylistHeader}
                                    data={data}
                                    moveList={moveList}
                                    findList={findList}
                                    imgAspect={aspect}
                                />
                            ))
                        ) : (
                            <EmptyTable>
                                선택된 플레이리스트가 없습니다.
                            </EmptyTable>
                        )}
                    </TableBody>
                    <StyledButton
                        title="저장하기"
                        bgColor={theme.colors.positiveBlue}
                        margin="60px 0 0"
                        width={305}
                        fontSize={16}
                        onClick={onSubmit}
                    />
                </E.SectionWrapper>
            </FlexBox>
        </InfoSection>
    );
};

export default PlaylistSection;
